import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { pageview, trackScroll, trackButtonClick, ButtonLabels } from "./gtags.client";

/**
 * Custom GA hook for pageview, scroll, and delegated button clicks
 */
export function useGoogleAnalytics() {
  const location = useLocation();
  const scrollTracked = useRef<number[]>([]);

  // Track page views
  useEffect(() => {
    pageview(location.pathname + location.search);
    scrollTracked.current = [];
  }, [location.pathname, location.search]);

  // Track scroll thresholds
  useEffect(() => {
    const handleScroll = () => {
      const scrollPercent = Math.round(
        (window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100
      );

      [25, 50, 75, 100].forEach((threshold) => {
        if (scrollPercent >= threshold && !scrollTracked.current.includes(threshold)) {
          trackScroll(threshold);
          scrollTracked.current.push(threshold);
        }
      });
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Delegated button click tracking via data-ga-label
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target) return;

      // Look for the nearest element with data-ga-label
      const buttonEl = target.closest<HTMLElement>("[data-ga-label]");
      if (buttonEl) {
        const label = buttonEl.dataset.gaLabel as keyof typeof ButtonLabels;
        if (label && ButtonLabels[label]) {
          trackButtonClick(ButtonLabels[label]);
        }
      }
    };

    document.addEventListener("click", handleClick);
    return () => document.removeEventListener("click", handleClick);
  }, []);
}
